'use no memo';

import type { Cell, Column, Row } from '@tanstack/react-table';
import type { ChangeEvent, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';

import { useTanstackTableDynamicContext } from '@/components/features/manage/tanstack-table-dynamic/TanstackTableDynamicProvider';
import { Button } from '@/components/ui/button';
import { DatePickerCalendar } from '@/components/ui/date-picker';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Option type for select fields.
 * @property {string} label - Display label.
 * @property {string} value - Option value.
 */
interface SelectOptions {
  label: string;
  value: string;
}

/**
 * Props for TanstackTableDynamicCell.
 * @property {Cell<TData, TValue>} cell - The cell object.
 * @property {Row<TData>} row - The row object.
 * @property {Column<TData, TValue>} column - The column object.
 * @property {() => TValue} getValue - Function to get cell value.
 * @property {'text'|'select'|'number'|'date'|'phone'|'email'} type - Cell type.
 * @property {SelectOptions[]} [options] - Options for select type.
 * @property {(value?: TValue) => ReactNode|string|undefined} [viewCell] - Custom cell view renderer.
 * @property {string} [placeholder] - Placeholder text.
 */
interface DataTableDynamicCell<TData, TValue> {
  cell: Cell<TData, TValue>;
  row: Row<TData>;
  column: Column<TData, TValue>;
  getValue: () => TValue;
  type: 'text' | 'select' | 'number' | 'date' | 'phone' | 'email';
  options?: SelectOptions[];
  viewCell?: (value?: TValue) => ReactNode | undefined | string;
  placeholder?: string;
}

/**
 * Form data for cell editing.
 */
interface DataTableDynamicCellForm {
  value: string | number | undefined;
}

export default function TanstackTableDynamicCell<TData, TValue>(dataTableDynamicCell: DataTableDynamicCell<TData, TValue>) {
  const { row, column, type, options, getValue, viewCell, placeholder } = dataTableDynamicCell;
  const { editedRows, updateData } = useTanstackTableDynamicContext();

  const isViewCell = viewCell !== undefined;

  const initialValue = getValue();

  const onBlur = (event: ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value;
    updateData(row.index, column.id, eventValue || null);
  };

  const onSubmit = (data: DataTableDynamicCellForm) => {
    updateData(row.index, column.id, data.value);
  };

  const form = useForm<DataTableDynamicCellForm>({
    mode: 'all',
    values: {
      value: initialValue as string,
    },
    defaultValues: {
      value: initialValue as string,
    },
  });

  let content;

  if (editedRows[row.index]) {
    if (type === 'text') {
      content = (
        <FormField
          name={'value'}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder={placeholder} onBlur={onBlur} className={'min-w-36'} />
              </FormControl>
            </FormItem>
          )}
        />
      );
    }
    if (type === 'select') {
      content = (
        <FormField
          control={form.control}
          name={'value'}
          render={({ field }) => (
            <FormItem>
              <Select
                defaultValue={field.value?.toString()}
                onValueChange={(newValue) => {
                  field.onChange(newValue);
                  onSubmit(newValue ? { value: newValue.toString() } : { value: newValue });
                }}
              >
                <FormControl>
                  <SelectTrigger className={'min-w-38'}>
                    <SelectValue placeholder="Wybierz..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    {options?.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      );
    }
    if (type === 'email') {
      content = (
        <FormField
          name={'value'}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder={placeholder} onBlur={onBlur} className={'min-w-36'} />
              </FormControl>
            </FormItem>
          )}
        />
      );
    }
    if (type === 'phone') {
      content = (
        <FormField
          name={'value'}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)} placeholder={placeholder} onBlur={onBlur} className={'min-w-36'} />
              </FormControl>
            </FormItem>
          )}
        />
      );
    }
    if (type === 'date') {
      content = (
        <FormField
          control={form.control}
          name={'value'}
          render={({ field }) => {
            return (
              <FormItem className="w-full">
                <FormControl>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant={'outline'} className={'min-w-30 font-normal'}>
                        <span className={cn('truncate', !field.value && 'text-muted-foreground')}>{field.value ? format(field.value, 'dd/MM/yyyy') : 'Wybierz datę'}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto border-0 p-0" align="start">
                      <DatePickerCalendar
                        {...field}
                        selected={field.value ? new Date(field.value) : null}
                        onSelect={(date) => {
                          const iso = date.toISOString();
                          field.onChange(iso);
                          updateData(row.index, column.id, iso);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </FormControl>
              </FormItem>
            );
          }}
        />
      );
    }
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {content}
        </form>
      </Form>
    );
  } else {
    return <>{isViewCell ? viewCell(initialValue) : (initialValue as string)}</>;
  }
}
